import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	isDevMode,
	provideAppInitializer,
	provideBrowserGlobalErrorListeners,
	provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalModule,
	MsalService,
} from '@azure/msal-angular';
import {
	BrowserCacheLocation,
	Configuration,
	InteractionType,
	IPublicClientApplication,
	LogLevel,
	PublicClientApplication,
} from '@azure/msal-browser';
import { provideTransloco } from '@jsverse/transloco';
import { environment } from '../environments/environment';
import { ENVIRONMENT } from '../environments/environment.interface';
import { providePermissionApiService } from './api/services/permission.api';
import { routes } from './app.routes';
import { initializeAuth } from './core/auth/initializer';
import { provideErrorHandler } from './core/classes/error-handler.class';
import { provideErrorService } from './core/services/error.service';
import { provideLoading } from './core/services/loading.service';
import { provideRecordManager } from './core/services/record.provide';
import { provideRepresentationMode } from './core/services/representation-mode.service';
import { provideTenantFeatures } from './core/services/tenant-feature.service';
import { APP_INSIGHTS, createApplicationInsights } from './shared/logging/appinsights.factory';
import { correlationInterceptor } from './shared/logging/correlation.interceptor';
import { LOGGER, LogService } from './shared/logging/log.service';
import { provideStorage } from './shared/storage/storage.providers';
import { DatabaseViewStorage } from './features/database/shared/services/database-view-storage.service';
import { TranslocoHttpLoader } from './transloco-loader';

export const msalConfig: Configuration = {
	auth: {
		clientId: environment.auth.clientId, // This is the ONLY mandatory field that you need to supply.
		authority: environment.auth.authority, // Replace the placeholder with your tenant subdomain
		knownAuthorities: environment.auth.knownAuthorities, // Required for B2C, optional for AAD. Array of domains that will be treated as "trusted" when processing tokens.
		redirectUri: environment.auth.redirectUri, // Points to window.location.origin by default. You must register this URI on Microsoft Entra admin center/App Registration.
		postLogoutRedirectUri: environment.auth.postLogoutRedirectUri, // Points to window.location.origin by default.
	},
	cache: {
		cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
	},
	system: {
		loggerOptions: {
			loggerCallback(logLevel: LogLevel, message: string) {
				// eslint-disable-next-line no-console
				console.log(message);
			},
			logLevel: LogLevel.Error,
			piiLoggingEnabled: false,
		},
	},
};
/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication(msalConfig);
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MsalGuardConfigurationFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: loginRequest,
	};
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: [],
};

export const appConfig: ApplicationConfig = {
	providers: [
		provideErrorHandler,
		{
			provide: APP_INSIGHTS,
			useFactory: createApplicationInsights,
		},
		{
			provide: LOGGER,
			useClass: LogService,
		},
		provideLoading,
		provideBrowserGlobalErrorListeners(),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes),
		provideHttpClient(withInterceptors([correlationInterceptor])),
		provideRecordManager(),
		provideRepresentationMode(),
		provideTenantFeatures,
		importProvidersFrom(MsalModule),
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MsalGuardConfigurationFactory,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true,
		},
		MsalService,
		MsalBroadcastService,
		MsalGuard,
		provideTransloco({
			config: {
				availableLangs: ['de'],
				defaultLang: 'de',
				// Remove this option if your application doesn't support changing language in runtime.
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
			},
			loader: TranslocoHttpLoader,
		}),
		provideAppInitializer(initializeAuth),
		// Subscribes the database view owner to session invalidation before any logout can run.
		provideAppInitializer(() => {
			inject(DatabaseViewStorage);
		}),
		provideStorage(),
		provideErrorService,
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		providePermissionApiService,
	],
};
